import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Play, Trash2, Plus, Film, Clock, Loader2, AlertTriangle, Bookmark, Link2, Shuffle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listVideos, deleteVideo, mediaUrl, savePresetFromVideo, errText } from "../lib/api";
import { PresetManager } from "../components/PresetManager";
import RemixDialog from "../components/RemixDialog";

const StatusBadge = ({ status }) => {
  const map = {
    completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    processing: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    failed: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return (
    <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full border capitalize ${map[status] || map.processing}`}>
      {status}
    </span>
  );
};

export default function Library() {
  const [videos, setVideos] = useState(null);
  const navigate = useNavigate();

  const load = () => listVideos().then(setVideos).catch(() => setVideos([]));
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    await deleteVideo(id);
    toast.success("Deleted");
    load();
  };

  const [savingId, setSavingId] = useState(null);
  const [remixTarget, setRemixTarget] = useState(null);
  const remix = (e, v) => {
    e.stopPropagation();
    e.preventDefault();
    setRemixTarget(v);
  };
  const [presetVersion, setPresetVersion] = useState(0);
  const saveAsPreset = async (e, v) => {
    e.stopPropagation();
    e.preventDefault();
    setSavingId(v.id);
    try {
      const p = await savePresetFromVideo(v.id);
      setPresetVersion((n) => n + 1);
      const kept = p.affiliate?.url ? " — affiliate link kept" : "";
      toast.success(`Saved "${p.name}" as a preset${kept}`, {
        action: { label: "New video from it", onClick: () => navigate(`/studio?preset=${p.id}`) },
      });
    } catch (err) { toast.error(errText(err)); }
    setSavingId(null);
  };

  if (videos === null) {
    return <div className="flex items-center justify-center py-40 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="eyebrow">Archive gallery</span>
          <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Your video library</h1>
        </div>
        <Button data-testid="library-new-btn" onClick={() => navigate("/studio")} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-5 font-semibold transition-colors">
          <Plus className="h-4 w-4 mr-1.5" /> New ad
        </Button>
      </div>

      <PresetManager refreshKey={presetVersion} />
      <RemixDialog video={remixTarget} open={!!remixTarget} onOpenChange={(o) => { if (!o) setRemixTarget(null); }} />

      {videos.length === 0 ? (
        <div className="mt-16 flex flex-col items-center justify-center text-center py-20 rounded-3xl border border-dashed border-white/10 bg-[#11141E]/50">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FF453A]/12 border border-[#FF453A]/25">
            <Film className="h-8 w-8 text-[#FF6A5E]" />
          </span>
          <h3 className="mt-5 font-display font-bold text-xl">No ads yet</h3>
          <p className="mt-2 text-slate-400 max-w-sm">Create your first faceless video ad and it'll show up here, ready to replay and download.</p>
          <Button data-testid="empty-create-btn" onClick={() => navigate("/studio")} className="mt-6 bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-6 py-5 font-semibold transition-colors">
            Create an ad
          </Button>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {videos.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.4) }}
            >
              <Link
                to={`/video/${v.id}`}
                data-testid={`video-card-${v.id}`}
                className="group block rounded-2xl overflow-hidden border border-white/10 bg-[#11141E] hover:border-[#FF453A]/40 transition-colors"
              >
                <div className="relative aspect-[9/16] bg-black">
                  {v.thumb_path ? (
                    <img src={mediaUrl(v.thumb_path)} alt={v.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-slate-600">
                      {v.status === "processing" ? <Loader2 className="h-8 w-8 animate-spin" /> : <Film className="h-8 w-8" />}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                  <div className="absolute top-2.5 left-2.5"><StatusBadge status={v.status} /></div>
                  <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {v.status === "completed" && (
                      <button
                        data-testid={`remix-${v.id}`}
                        onClick={(e) => remix(e, v)}
                        title="Remix — new narration, angle & visual style"
                        aria-label="Remix video"
                        className="h-7 w-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-[#FF453A]/80 transition-colors"
                      >
                        <Shuffle className="h-3.5 w-3.5 text-white" />
                      </button>
                    )}
                    <button
                      data-testid={`save-preset-${v.id}`}
                      onClick={(e) => saveAsPreset(e, v)}
                      disabled={savingId === v.id}
                      title={v.affiliate?.enabled && v.affiliate?.url ? "Save as preset (keeps affiliate link)" : "Save as preset"}
                      aria-label="Save as preset"
                      className="h-7 w-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-[#FF453A]/80 transition-colors"
                    >
                      {savingId === v.id ? <Loader2 className="h-3.5 w-3.5 text-white animate-spin" /> : <Bookmark className="h-3.5 w-3.5 text-white" />}
                    </button>
                    <button data-testid={`delete-${v.id}`} onClick={(e) => remove(e, v.id)} aria-label="Delete video" className="h-7 w-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-red-500/80 transition-colors">
                      <Trash2 className="h-3.5 w-3.5 text-white" />
                    </button>
                  </div>
                  {v.affiliate?.enabled && v.affiliate?.url && (
                    <span data-testid={`affiliate-badge-${v.id}`} className="absolute top-11 left-2.5 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-[#FF453A]/30 bg-black/60 text-[#FF8A80]">
                      <Link2 className="h-2.5 w-2.5" /> Affiliate
                    </span>
                  )}
                  {v.status === "completed" && (
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90"><Play className="h-5 w-5 text-black ml-0.5" fill="black" /></span>
                    </span>
                  )}
                  <div className="absolute bottom-0 inset-x-0 p-3">
                    <h4 className="font-display font-bold text-sm text-white truncate">{v.title}</h4>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                      <Clock className="h-3 w-3" /> {v.duration}s
                      <span className="capitalize">· {v.tone.replace("_", " ")}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
